import { useContext, useEffect, useState } from 'react';
import { Box, CircularProgress, Paper, Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { PageHeader } from '../../../../shared/components/PageHeader';
import { EmpresaSelect } from '../../../../shared/components/EmpresaSelect';
import { AuthContext } from '../../../../shared/auth/AuthContext';
import { CompanyNotasFiscaisManager } from '../CompanyNotasFiscaisManager';
import { loadNfEmpresaGridState } from '../nfEmpresaGridState';

/**
 * Página de gestão de NF Empresa fora do CompanyFormDialog. Mantém a empresa
 * selecionada na querystring `?empresaId=X` para deep-link e refresh-friendly.
 */
export function NotasFiscaisEmpresaPage(): JSX.Element {
  const { t } = useTranslation();
  const auth = useContext(AuthContext);
  const userId = auth?.status === 'bootstrapping' ? undefined : (auth?.user?.externalId ?? null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [empresaPronta, setEmpresaPronta] = useState(false);
  const empresaIdParam = searchParams.get('empresaId');
  const empresaId = empresaIdParam ? Number.parseInt(empresaIdParam, 10) : null;
  const empresaIdValido = empresaId !== null && Number.isFinite(empresaId) && empresaId > 0;

  useEffect(() => {
    if (userId === undefined || empresaPronta) return;
    if (!empresaIdParam && userId != null) {
      const stored = loadNfEmpresaGridState(userId);
      if (stored?.empresaId) {
        const next = new URLSearchParams(searchParams);
        next.set('empresaId', String(stored.empresaId));
        setSearchParams(next, { replace: true });
      }
    }
    setEmpresaPronta(true);
  }, [userId, empresaPronta, empresaIdParam, searchParams, setSearchParams]);

  function setEmpresaId(id: number | null): void {
    const next = new URLSearchParams(searchParams);
    if (id === null) {
      next.delete('empresaId');
    } else {
      next.set('empresaId', String(id));
    }
    setSearchParams(next, { replace: true });
  }

  return (
    <Box>
      <PageHeader
        title={t('features.empresa.notasFiscaisPage.title')}
        subtitle={t('features.empresa.notasFiscaisPage.subtitle')}
      />
      <Stack spacing={3}>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <EmpresaSelect
            value={empresaIdValido ? empresaId : null}
            onChange={(id) => setEmpresaId(id)}
            label={t('features.empresa.notasFiscaisPage.empresaSelectLabel')}
            placeholder={t('features.empresa.notasFiscaisPage.empresaSelectPlaceholder')}
          />
        </Paper>

        {empresaPronta ? (
          <CompanyNotasFiscaisManager
            companyId={empresaIdValido ? empresaId : null}
            onClearEmpresa={() => setEmpresaId(null)}
          />
        ) : (
          <CircularProgress size={24} />
        )}
      </Stack>
    </Box>
  );
}
